import { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown } from 'lucide-react';

/**
 * SearchableSelect — generic, controlled searchable dropdown.
 *
 * Props:
 *   placeholder   : string
 *   items          : array of objects (must have .name, optionally .department)
 *   value          : currently selected item (or null)
 *   onChange        : (item) => void
 *   renderItem     : (item, isActive, accentColor) => JSX
 *   renderSelected : (item) => JSX
 *   accentColor    : string (default '#6366f1')
 *   isOpen         : boolean – controlled open state
 *   onToggle       : () => void – toggle open/close
 *   onClose        : () => void – close dropdown
 */
const SearchableSelect = ({ placeholder, items, value, onChange, renderItem, renderSelected, accentColor = '#6366f1', isOpen, onToggle, onClose, onSearch }) => {
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  // Reset search when closed
  useEffect(() => {
    if (!isOpen) setSearch('');
  }, [isOpen]);

  // Call onSearch prop if provided when search changes
  useEffect(() => {
    if (onSearch) {
      const timeoutId = setTimeout(() => {
        onSearch(search);
      }, 500); // 500ms debounce
      return () => clearTimeout(timeoutId);
    }
  }, [search, onSearch]);

  const filtered = onSearch 
    ? items // If onSearch is provided, we assume items are already filtered by the server
    : items.filter(
        (it) =>
          it.name.toLowerCase().includes(search.toLowerCase()) ||
          (it.department || '').toLowerCase().includes(search.toLowerCase())
      );

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 14px',
          borderRadius: '10px',
          border: isOpen ? `2px solid ${accentColor}` : '1.5px solid #e2e8f0',
          background: '#fafafa',
          cursor: 'pointer',
          textAlign: 'left',
          boxShadow: isOpen ? `0 0 0 3px ${accentColor}20` : 'none',
          transition: 'all 0.2s',
          color: value ? '#0f172a' : '#94a3b8',
          fontSize: '13px',
          fontWeight: value ? '600' : '400',
        }}
      >
        <span style={{ flex: 1 }}>{value ? renderSelected(value) : placeholder}</span>
        <ChevronDown size={14} color="#94a3b8" style={{ flexShrink: 0, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 200,
            background: '#fff',
            borderRadius: '14px',
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            animation: 'dropFadeIn 0.18s ease',
          }}
        >
          {/* Search inside dropdown */}
          <div style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px', background: '#fafafa' }}>
            <Search size={14} color="#94a3b8" />
            <input
              autoFocus
              type="text"
              placeholder="Cari..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '12.5px', background: 'transparent', color: '#0f172a' }}
            />
          </div>

          {/* Items list */}
          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: '#94a3b8' }}>Tidak ada hasil</div>
            ) : filtered.map((item) => {
              const isActive = value?.id === item.id;
              return (
                <div
                  key={item.id}
                  onClick={() => { onChange(item); onClose(); }}
                  style={{
                    padding: '10px 14px',
                    cursor: 'pointer',
                    background: isActive ? `${accentColor}12` : 'transparent',
                    borderLeft: isActive ? `3px solid ${accentColor}` : '3px solid transparent',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {renderItem(item, isActive, accentColor)}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
